import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  Divider,
  IconButton,
  TextField,
  Button,
} from '@mui/material';
import {
  GitHub,
  Twitter,
  LinkedIn,
  Email,
  School,
  Language,
  Security,
  Send,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const Footer: React.FC = () => {
  const { t } = useTranslation();

  const footerSections = [
    {
      title: t('footer.quickLinks'), 
      links: [
        { name: t('footer.courses'), href: '/courses' },
        { name: t('footer.about'), href: '/about' },
        { name: t('footer.contact'), href: '/contact' },
        { name: t('footer.blog'), href: '/blog' },
      ],
    },
    {
      title: t('footer.support'),
      links: [
        { name: t('footer.support'), href: '/support' },
        { name: t('common.help'), href: '/help' },
        { name: 'FAQ', href: '/faq' },
        { name: t('common.guide'), href: '/guide' },
      ],
    },
    {
      title: t('footer.web3Features'),
      links: [
        { name: t('footer.nftCertificates'), href: '/web3/certificates' },
        { name: t('footer.tokenRewards'), href: '/web3/tokens' },
        { name: t('footer.daoGovernance'), href: '/web3/dao' },
        { name: t('common.web3'), href: '/web3' },
      ],
    },
  ];

  const socialMedias = [
    { name: 'GitHub', icon: <GitHub />, url: 'https://github.com' },
    { name: 'Twitter', icon: <Twitter />, url: 'https://twitter.com' },
    { name: 'LinkedIn', icon: <LinkedIn />, url: 'https://linkedin.com' },
    { name: 'Email', icon: <Email />, url: 'mailto:info@uai.edu' },
  ];

  const features = [
    {
      icon: <School />,
      title: t('home.features.aiEducation.title'),
      description: t('home.features.aiEducation.description'),
    },
    {
      icon: <Language />,
      title: t('home.features.web3Integration.title'),
      description: t('home.features.web3Integration.description'),
    },
    {
      icon: <Security />,
      title: t('footer.security.title'),
      description: t('footer.security.description'),
    },
  ];

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: 'grey.900',
        color: 'white',
        py: 6,
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg">
        {/* 特色功能展示 */}
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  backgroundColor: 'grey.800',
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: 'primary.main',
                    borderRadius: '50%',
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {feature.icon}
                </Box>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="grey.400">
                    {feature.description}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 4, borderColor: 'grey.700' }} />

        {/* 主要内容区域 */}
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {/* 关于我们 */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <School sx={{ fontSize: 40, color: 'primary.main' }} />
              <Box>
                <Typography variant="h6" component="div">
                  {t('header.siteName')}
                </Typography>
                <Typography variant="body2" color="grey.400">
                  University of Artificial Intelligence
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" color="grey.400" sx={{ mb: 3 }}>
              {t('footer.description')}
            </Typography>
            
            {/* 社交媒体链接 */}
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
              {t('footer.followUs')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {socialMedias.map((social, index) => (
                <IconButton
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'grey.400',
                    '&:hover': {
                      color: 'primary.main',
                      backgroundColor: 'grey.800',
                    },
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Box>
          </Grid>

          {/* 导航链接 */}
          {footerSections.map((section, index) => (
            <Grid item xs={12} sm={4} md={2.67} key={index}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                {section.title}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {section.links.map((link, linkIndex) => (
                  <Link
                    key={linkIndex}
                    href={link.href}
                    color="inherit"
                    underline="hover"
                    sx={{
                      color: 'grey.400',
                      '&:hover': {
                        color: 'primary.main',
                      },
                    }}
                  >
                    {link.name}
                  </Link>
                ))}
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* 新闻订阅 */}
        <Box
          sx={{
            backgroundColor: 'grey.800',
            borderRadius: 2,
            p: 3,
            mb: 4,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                {t('footer.newsletter')}
              </Typography>
              <Typography variant="body2" color="grey.400">
                {t('footer.newsletterDescription')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  placeholder={t('footer.emailPlaceholder')}
                  variant="outlined"
                  size="small"
                  sx={{
                    flexGrow: 1,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'grey.700',
                      '& fieldset': { borderColor: 'grey.600' },
                      '&:hover fieldset': { borderColor: 'grey.500' },
                      '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                    },
                    '& .MuiInputBase-input': { color: 'white' },
                    '& .MuiInputBase-input::placeholder': { color: 'grey.400' },
                  }}
                />
                <Button
                  variant="contained"
                  endIcon={<Send />}
                  sx={{ flexShrink: 0 }}
                >
                  {t('footer.subscribe')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 4, borderColor: 'grey.700' }} />

        {/* 版权信息 */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="body2" color="grey.400">
            {t('footer.copyright')}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Link href="/privacy" color="grey.400" underline="hover">
              {t('auth.register.privacyLink')}
            </Link>
            <Link href="/terms" color="grey.400" underline="hover">
              {t('auth.register.termsLink')}
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer; 